import React, {useEffect, useState} from "react";
import {
    UniButton,
    UniCol,
    UniRow,
    UniForm, IntlMessageValue, IntlMessage, UniSpinner
} from "../../../../@WUM/core/Elements";
import UniChecks from "../../../../@WUM/core/Elements/Form/Elements/Check";
import SunEditorConfig from "../../../ticketsystem/modules/components/SuneditorConfig";
import SunEditor from "suneditor-react";
import {makeSubmitHandler} from "./save/SubmitHandler";
import {useToast} from "../../../../@WUM/core/WebsocketNotification/ToastProvider/ToastProvider";
import {ResponseAutoClockOutSettings} from "../../API/response/settingstimetracking";
import {TimetrackingInterface} from "./Interfaces/Timetracking.Interface";


interface SettingsState {
    forceAutoClockOut: boolean;
    timeReminder: string;
    timeAfterAutoClockOut: string;
    activateClockOutWarningPopup: boolean;
    clockOutWarningPopupContent: string;
    activateClockOutWarningMail: boolean;
    clockOutWarningMailContent: string;
    clockOutWarningMailTitle: string;
}

const AutoClockOutSettings = ({
                                  onChangeUnsaved,
                                  userMode = false,
                                  userId,
                              }: TimetrackingInterface) => {

    const [unsaved, setUnsaved] = useState(false);

    const {showToast} = useToast();

    const markUnsaved = () => {
        if (!unsaved) {
            setUnsaved(true);
            onChangeUnsaved?.(true);
        }
    };

    const Response = ResponseAutoClockOutSettings(userId);

    useEffect(() => {
        if (!Response) return;
        const r = (Response as any).data ?? Response;

        setSettings({
            forceAutoClockOut: !!r.forceAutoClockOut,
            timeReminder: String(r.timeReminder ?? ""),
            timeAfterAutoClockOut: String(r.timeAfterAutoClockOut ?? ""),
            activateClockOutWarningPopup: !!r.activateClockOutWarningPopup,
            clockOutWarningPopupContent: r.clockOutWarningPopupContent ?? "",
            activateClockOutWarningMail: !!r.activateClockOutWarningMail,
            clockOutWarningMailContent: r.clockOutWarningMailContent ?? "",
            clockOutWarningMailTitle: r.clockOutWarningMailTitle ?? "",
        });


        setUnsaved(false);
        onChangeUnsaved?.(false);
    }, [Response]);

    const [validated, setValidated] = useState<boolean>(false);

    const [settings, setSettings] = useState<SettingsState | null>(null);

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = event.target;

        if (
            name === "forceAutoClockOut" ||
            name === "activateClockOutWarningPopup" ||
            name === "activateClockOutWarningMail"
        ) {
            setSettings(prev => (prev ? {...prev, [name]: checked} : prev));
            markUnsaved();
        }
    };

    const setEditorContent = (fieldName: keyof SettingsState, html: string) => {
        setSettings(prev => (prev ? {...prev, [fieldName]: html as any} : prev)); // <-- null-safe
        markUnsaved();
    };

    const finalRoute: string = userMode && userId
        ? `work/autoclocksettings/save/?id=${userId}`
        : "work/autoclocksettings/save/";


    const handleSubmit = makeSubmitHandler({
        showToast,
        route: finalRoute,
        buildPayload: (form) => ({
            id: (Response as any)?.id,
            forceAutoClockOut: settings!.forceAutoClockOut,
            timeReminder: form.timeReminder.value,
            timeAfterAutoClockOut: form.timeAfterAutoClockOut.value,
            ...(!userMode && {
                activateClockOutWarningPopup: settings!.activateClockOutWarningPopup,
                clockOutWarningPopupContent: settings!.clockOutWarningPopupContent,
                activateClockOutWarningMail: settings!.activateClockOutWarningMail,
                clockOutWarningMailContent: settings!.clockOutWarningMailContent,
                clockOutWarningMailTitle: form.clockOutWarningMailTitle.value,
            })
        }),
        setUnsaved,
        onChangeUnsaved,
        setValidated,
    });

    if (Response === undefined || !settings) return <><UniSpinner/></>;


    return (
        <>
            <UniForm validated={validated} Submit={handleSubmit} className={"p-4 worktime-settings"}>
                <div className={"border border-light-subtle rounded py-2 my-2 row "}>
                    <p>
                        {IntlMessage({
                            locale: 'de',
                            messageId: 'time.settings.settings.force-auto-clock-out-explanation',
                            Store: ''
                        })}
                    </p>
                </div>

                <UniRow>
                    <UniCol>
                        <UniChecks
                            id="forceAutoClockOut"
                            type="switch"
                            label={IntlMessageValue("", "time.settings.force-auto-clock-out", "")}
                            name="forceAutoClockOut"
                            checked={settings?.forceAutoClockOut}
                            class="uni-switch ps-0 my-3"
                            onChange={handleSwitchChange}
                            info={{text: "time.settings.settings.force-auto-clock-out.helper"}}
                        />
                    </UniCol>
                    <UniCol>
                        <UniForm.InputGroup class={"mb-md-0  my-1"}>
                            <UniForm.InputGroupText class={""}
                                                    text={IntlMessageValue("", "time.settings.break-out.warning-trigger-minutes", "")}/>
                            <UniForm.FormControl
                                type="number"
                                name="timeReminder"
                                defaultValue={settings?.timeReminder}
                                required
                                step={0.5}
                            />
                            <UniForm.InputGroupText class={""} text={"min"}/>
                            <UniForm.InputGroupHelpText text={"time.settings.break-out.warning-trigger-minutes.helper"}
                                                        class={"p-0 pe-1"} Store={""}/>
                        </UniForm.InputGroup>
                    </UniCol>
                    <UniCol>
                        <UniForm.InputGroup class={"mb-md-0  my-1"}>
                            <UniForm.InputGroupText class={""}
                                                    text={IntlMessageValue("", "time.settings.time-after-auto-clock-out", "")}/>
                            <UniForm.FormControl
                                type="number"
                                name="timeAfterAutoClockOut"
                                defaultValue={settings?.timeAfterAutoClockOut}
                                required
                                step={0.5}
                            />
                            <UniForm.InputGroupText class={""} text={"min"}/>
                            <UniForm.InputGroupHelpText text={"time.settings.time-after-auto-clock-out.helper"}
                                                        class={"p-0 pe-1"} Store={""}/>
                        </UniForm.InputGroup>
                    </UniCol>
                </UniRow>
                {!userMode && (
                    <>
                        <UniRow class={"border border-light-subtle rounded py-2 my-3"}>
                            <UniCol xs={12}>
                                <UniChecks
                                    id="activateClockOutWarningPopup"
                                    type="switch"
                                    label={IntlMessageValue("", "time.settings.activate-clock-out-warning-popup", "")}
                                    name="activateClockOutWarningPopup"
                                    checked={settings?.activateClockOutWarningPopup}
                                    class="uni-switch ps-0 my-3"
                                    onChange={handleSwitchChange}
                                    info={{text: "time.settings.activate-clock-out-warning-popup.helper"}}
                                />
                            </UniCol>
                            <UniCol xs={12}>
                                <p>{IntlMessage({
                                    locale: 'de',
                                    messageId: 'time.settings.clock-out-warning-popup-content',
                                    Store: ''
                                })}</p>
                                <SunEditor
                                    key={'test'}

                                    name={'clockOutWarningPopupContent'}
                                    setContents={settings.clockOutWarningPopupContent}
                                    setOptions={{
                                        buttonList: SunEditorConfig(),
                                        attributesWhitelist: {
                                            div: 'id',
                                            p: 'id',
                                            h: 'id'
                                        }
                                    }}
                                    height={'150px'}
                                    onChange={(value) => setEditorContent('clockOutWarningPopupContent', value)}
                                />
                            </UniCol>
                        </UniRow>

                        <UniRow class={"border border-light-subtle rounded py-2"}>
                            <UniCol xs={12}>
                                <UniChecks
                                    id="activateClockOutWarningMail"
                                    type="switch"
                                    label={IntlMessageValue("", "time.settings.activate-clock-out-warning-mail", "")}
                                    name="activateClockOutWarningMail"
                                    checked={settings?.activateClockOutWarningMail}
                                    class="uni-switch ps-0 mb-3 "
                                    onChange={handleSwitchChange}
                                    info={{text: "time.settings.activate-clock-out-warning-mail.helper"}}
                                />
                            </UniCol>
                            <UniCol xs={12}>
                                <UniForm.InputGroup class={"mb-3"}>
                                    <UniForm.InputGroupText class={""}
                                                            text={IntlMessageValue("", "time.settings.clock-out-warning-mail-title", "")}/>
                                    <UniForm.FormControl
                                        type="text"
                                        name="clockOutWarningMailTitle"
                                        defaultValue={settings?.clockOutWarningMailTitle}
                                        required
                                    />
                                    <UniForm.InputGroupHelpText
                                        text={"time.settings.clock-out-warning-mail-title.helper"}
                                        class={"p-0 pe-1"} Store={""}/>
                                </UniForm.InputGroup>
                            </UniCol>
                            <UniCol xs={12}>
                                <p>{IntlMessage({
                                    locale: 'de',
                                    messageId: 'time.settings.clock-out-warning-mail-content',
                                    Store: ''
                                })}</p>
                                <SunEditor
                                    key={'test'}

                                    name={'clockOutWarningMailContent'}
                                    setContents={settings.clockOutWarningMailContent}
                                    setOptions={{
                                        buttonList: SunEditorConfig(),
                                        attributesWhitelist: {
                                            div: 'id',
                                            p: 'id',
                                            h: 'id'
                                        }
                                    }}
                                    height={'150px'}
                                    onChange={(value) => setEditorContent('clockOutWarningMailContent', value)}
                                />
                            </UniCol>
                        </UniRow>
                    </>
                )}

                <UniButton type="submit" class={"float-end"}>{IntlMessage({
                    locale: 'de',
                    messageId: 'common.save',
                    Store: ''
                })}</UniButton>
            </UniForm>
        </>
    );
};

export default AutoClockOutSettings;
